/**
 * What a translation value may be: ONE pure rule, shared by the server (which refuses a value that breaks it) and the
 * editor (which tells the person before they save). Nothing here reads a catalogue or a database: the caller hands in
 * the value, the reference (English) text, the text of the locale's own file and the plural siblings of the key.
 *
 * Errors stop the save, warnings do not. Both lists come out in the order of their code lists below and, inside a code,
 * by position in the text (the reference's for `MISSING_PLACEHOLDER` and `PLACEHOLDER_FORMAT_CHANGED`, the value's for the
 * rest), so a caller can compare the whole list. An issue carries a placeholder name, a piece of markup or of contact data
 * cut at 200 characters: never the whole text.
 */

import { oneCoversMoreThanOne, pluralCategoriesOf, splitPluralKey } from './plural-forms.js'

export const TRANSLATION_VALUE_ERROR_CODES = [
  'MISSING_PLACEHOLDER',
  'UNKNOWN_PLACEHOLDER',
  'PLACEHOLDER_FORMAT_CHANGED',
  'MALFORMED_PLACEHOLDER',
  'NESTING_NOT_ALLOWED',
  'MARKUP_NOT_ALLOWED',
  'MARKUP_UNBALANCED',
  'EMPTY_VALUE',
  'TOO_LONG',
  'CONTROL_CHARACTERS',
] as const

export const TRANSLATION_VALUE_WARNING_CODES = [
  'SAME_AS_BASE',
  'CONTAINS_CONTACT_DATA',
  'OUTER_WHITESPACE_CHANGED',
  'UNUSED_PLURAL_FORM',
] as const

export type TranslationValueErrorCode = (typeof TRANSLATION_VALUE_ERROR_CODES)[number]
export type TranslationValueWarningCode = (typeof TRANSLATION_VALUE_WARNING_CODES)[number]
export type TranslationValueIssueCode = TranslationValueErrorCode | TranslationValueWarningCode

/** The most characters a value may have. */
export const TRANSLATION_VALUE_MAX_LENGTH = 5000
/** A value may be at most this many times as long as its reference… */
export const TRANSLATION_VALUE_REFERENCE_RATIO = 3
/** …when the reference is longer than this (a short text may need much more room in another language). */
export const TRANSLATION_VALUE_RATIO_FLOOR = 200

export interface TranslationIssue {
  code: TranslationValueIssueCode
  /** The name of the `{{placeholder}}` the issue is about. */
  placeholder?: string
  /** The piece of markup (`<img src=x>`) the issue is about. */
  fragment?: string
  /** The length the value went over. */
  limit?: number
  /** `PLACEHOLDER_FORMAT_CHANGED`: the format the reference has (`number`, or `''` for none). */
  expected?: string
  /** What was found: the format of the value, the plural form, or the contact data (`CONTAINS_CONTACT_DATA`). */
  found?: string
}

export interface TranslationValidationInput {
  /** The locale the value is for: `es`, `ru`. */
  locale: string
  /** The key inside its namespace, without `ns:` (`shared.bulkActions.selected_other`). */
  key: string
  value: string
  /** The English text of the key. */
  referenceValue: string
  /** The text of the locale's own file for the key, or `null` when the file has none. */
  localeBaseValue: string | null
  /** The English texts of the OTHER plural forms of the same base key (their placeholders are known to the key). */
  pluralSiblingValues: readonly string[]
}

export interface TranslationValidationResult {
  errors: TranslationIssue[]
  warnings: TranslationIssue[]
}

const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z0-9_.]+)\s*(?:,\s*([^{}]*?)\s*)?\}\}/g
// A `<` followed by a letter, `/`, `!` or `?` opens a tag of ANY length, closed with `>` or not (a browser closes it at
// the next `<`): `<img src=x onerror=alert(1) <b>` has an `<img …` in it. `<` followed by a space or a digit
// (`a < b`, `<3`) is text.
const MARKUP_PATTERN = /<[\p{L}/!?][^<>]*>?/gu
/**
 * Event handlers (`onerror=`) and script URLs (`javascript:`) are refused wherever they are, inside a tag or not. The
 * script URL is looked for with tabs and line breaks removed, as a browser reads a URL (`java<TAB>script:`).
 */
const EVENT_HANDLER_PATTERN = /(?<![\p{L}\p{N}_])on[a-z]{2,}\s*=/giu
const SCRIPT_URL_PATTERN = /(?:java|vb)script\s*:/gi
/** An HTML-like tag: a name made of ASCII letters and digits, then attributes or nothing (`<strong>`, `</a>`). */
const TAG_PATTERN = /^<(\/?)([A-Za-z][A-Za-z0-9]*)(?:\s[^<>]*)?>$/
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
const URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi
const PHONE_PATTERN = /\+?\d(?:[ .-]?\d){8,14}/g

/** The most characters of the text an issue repeats (a long tag or URL is cut). */
const ISSUE_TEXT_MAX_LENGTH = 200

/** name → format (`''` when it has none; the first format a name appears with wins), in order of appearance. */
function placeholdersOf(text: string): Map<string, string> {
  const found = new Map<string, string>()
  for (const match of text.matchAll(PLACEHOLDER_PATTERN)) {
    if (!found.has(match[1])) found.set(match[1], match[2] ?? '')
  }
  return found
}

function hasControlCharacters(text: string): boolean {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index)
    // Tab and line feed are text; every other C0 character, DEL and C1 are not.
    if (code <= 0x08 || (code >= 0x0b && code <= 0x1f) || (code >= 0x7f && code <= 0x9f))
      return true
  }
  return false
}

function outerWhitespace(text: string): [string, string] {
  return [text.slice(0, text.length - text.trimStart().length), text.slice(text.trimEnd().length)]
}

/** The first match of any contact pattern in `value` that the reference does not already have, as written. */
function newContactData(value: string, referenceValue: string): string | undefined {
  for (const pattern of [EMAIL_PATTERN, URL_PATTERN, PHONE_PATTERN]) {
    for (const match of value.matchAll(pattern)) {
      if (!referenceValue.includes(match[0])) return match[0]
    }
  }
  return undefined
}

/** The names of the tags that some of `texts` closes (`<strong>…</strong>`): the ones whose balance is checked. */
function pairedTagNames(...texts: Array<string | null>): Set<string> {
  const names = new Set<string>()
  for (const text of texts) {
    if (text === null) continue
    for (const fragment of text.match(MARKUP_PATTERN) ?? []) {
      const tag = TAG_PATTERN.exec(fragment)
      if (tag?.[1] === '/') names.add(tag[2])
    }
  }
  return names
}

/**
 * Balance, by tag name over every fragment (a refused `<a href=…>` still opens an `<a>`): a tag in `paired` must be closed
 * the same number of times it is opened. `<br>` or `bat:<symbol>` have no closing form there and are not counted; `<x/>`
 * never is. Returns, per unbalanced tag in order of first appearance, its first surplus-side fragment.
 */
function unbalancedTagFragments(fragments: readonly string[], paired: Set<string>): string[] {
  const byName = new Map<string, { opens: string[]; closes: string[] }>()
  for (const fragment of fragments) {
    const tag = TAG_PATTERN.exec(fragment)
    if (!tag || !paired.has(tag[2]) || fragment.endsWith('/>')) continue
    const entry = byName.get(tag[2]) ?? { opens: [], closes: [] }
    if (tag[1] === '/') entry.closes.push(fragment)
    else entry.opens.push(fragment)
    byName.set(tag[2], entry)
  }
  const unbalanced: string[] = []
  for (const { opens, closes } of byName.values()) {
    if (opens.length > closes.length) unbalanced.push(opens[0])
    else if (closes.length > opens.length) unbalanced.push(closes[0])
  }
  return unbalanced
}

export function validateTranslationValue(
  input: TranslationValidationInput,
): TranslationValidationResult {
  const { locale, key, value, referenceValue, localeBaseValue, pluralSiblingValues } = input
  const pluralKey = splitPluralKey(key)
  const errors: Record<TranslationValueErrorCode, TranslationIssue[]> = {
    MISSING_PLACEHOLDER: [],
    UNKNOWN_PLACEHOLDER: [],
    PLACEHOLDER_FORMAT_CHANGED: [],
    MALFORMED_PLACEHOLDER: [],
    NESTING_NOT_ALLOWED: [],
    MARKUP_NOT_ALLOWED: [],
    MARKUP_UNBALANCED: [],
    EMPTY_VALUE: [],
    TOO_LONG: [],
    CONTROL_CHARACTERS: [],
  }

  // Placeholders.
  const referencePlaceholders = placeholdersOf(referenceValue)
  const valuePlaceholders = placeholdersOf(value)
  const knownToKey = new Set(referencePlaceholders.keys())
  for (const sibling of pluralSiblingValues) {
    for (const name of placeholdersOf(sibling).keys()) knownToKey.add(name)
  }
  // `count` may be left out of `_zero` («No results»), and of `_one` only where `one` is the number 1 and nothing
  // else: where it also covers 21 or 0 (Russian, French) the text is shown with those numbers and must carry it.
  const countMayBeOmitted =
    pluralKey?.form === 'zero' || (pluralKey?.form === 'one' && !oneCoversMoreThanOne(locale))
  for (const [name, expected] of referencePlaceholders) {
    const found = valuePlaceholders.get(name)
    if (found === undefined) {
      const countLeftOutOnPurpose = name === 'count' && countMayBeOmitted
      if (!countLeftOutOnPurpose) {
        errors.MISSING_PLACEHOLDER.push({ code: 'MISSING_PLACEHOLDER', placeholder: name })
      }
    } else if (found !== expected) {
      errors.PLACEHOLDER_FORMAT_CHANGED.push({
        code: 'PLACEHOLDER_FORMAT_CHANGED',
        placeholder: name,
        expected,
        found,
      })
    }
  }
  // Where `one` covers more than the number 1, the text of `_one` is shown with 21 or 0 too: it carries `{{count}}`
  // whenever the reference or any sibling form does, even when the English `_one` says «One item» in words.
  if (
    pluralKey?.form === 'one' &&
    oneCoversMoreThanOne(locale) &&
    !valuePlaceholders.has('count') &&
    !referencePlaceholders.has('count') &&
    pluralSiblingValues.some((sibling) => placeholdersOf(sibling).has('count'))
  ) {
    errors.MISSING_PLACEHOLDER.push({ code: 'MISSING_PLACEHOLDER', placeholder: 'count' })
  }
  for (const name of valuePlaceholders.keys()) {
    if (!knownToKey.has(name)) {
      errors.UNKNOWN_PLACEHOLDER.push({ code: 'UNKNOWN_PLACEHOLDER', placeholder: name })
    }
  }
  if (/\{\{|\}\}/.test(value.replace(PLACEHOLDER_PATTERN, ''))) {
    errors.MALFORMED_PLACEHOLDER.push({ code: 'MALFORMED_PLACEHOLDER' })
  }

  // Nesting (`$t(other.key)`): a value cannot reach into another key.
  if (value.includes('$t(')) errors.NESTING_NOT_ALLOWED.push({ code: 'NESTING_NOT_ALLOWED' })

  // Markup: only the tags, as written, that the reference or the locale's own file already has (so the name AND the
  // attributes are theirs, and a fragment that never closes is not a tag of theirs); never a handler or a script URL.
  const knownTags = new Set([
    ...(referenceValue.match(MARKUP_PATTERN) ?? []),
    ...(localeBaseValue?.match(MARKUP_PATTERN) ?? []),
  ])
  const fragments = value.match(MARKUP_PATTERN) ?? []
  // Tags, handlers and script URLs are one code: they come out by position in the value.
  const refused: Array<{ index: number; fragment: string }> = []
  for (const match of value.matchAll(MARKUP_PATTERN)) {
    if (!knownTags.has(match[0])) {
      refused.push({ index: match.index, fragment: match[0].trimEnd() })
    }
  }
  const withoutLineBreaks = value.replace(/[\t\n\r]/g, '')
  for (const [text, pattern] of [
    [value, EVENT_HANDLER_PATTERN],
    [withoutLineBreaks, SCRIPT_URL_PATTERN],
  ] as const) {
    for (const match of text.matchAll(pattern)) {
      if (!referenceValue.includes(match[0])) {
        refused.push({ index: match.index, fragment: match[0] })
      }
    }
  }
  for (const { fragment } of refused.sort((a, b) => a.index - b.index)) {
    errors.MARKUP_NOT_ALLOWED.push({
      code: 'MARKUP_NOT_ALLOWED',
      fragment: fragment.slice(0, ISSUE_TEXT_MAX_LENGTH),
    })
  }
  // Balance: only the tags the reference or the locale's own file closes (`<strong>…</strong>`).
  const paired = pairedTagNames(referenceValue, localeBaseValue)
  for (const fragment of unbalancedTagFragments(fragments, paired)) {
    errors.MARKUP_UNBALANCED.push({ code: 'MARKUP_UNBALANCED', fragment })
  }

  // Size and characters.
  // i18next paints an empty string as empty (`returnEmptyString`), it does not fall back: removing a text is the
  // `remove` action, never an empty value. The only empty value that is valid is the one the locale file has.
  if (value === '' && localeBaseValue !== '') {
    errors.EMPTY_VALUE.push({ code: 'EMPTY_VALUE' })
  }
  if (value.length > TRANSLATION_VALUE_MAX_LENGTH) {
    errors.TOO_LONG.push({ code: 'TOO_LONG', limit: TRANSLATION_VALUE_MAX_LENGTH })
  } else if (referenceValue.length > TRANSLATION_VALUE_RATIO_FLOOR) {
    const limit = TRANSLATION_VALUE_REFERENCE_RATIO * referenceValue.length
    if (value.length > limit) errors.TOO_LONG.push({ code: 'TOO_LONG', limit })
  }
  if (hasControlCharacters(value)) {
    errors.CONTROL_CHARACTERS.push({ code: 'CONTROL_CHARACTERS' })
  }

  const warnings: TranslationIssue[] = []
  if (localeBaseValue !== null && value === localeBaseValue) {
    warnings.push({ code: 'SAME_AS_BASE' })
  }
  const contact = newContactData(value, referenceValue)
  if (contact !== undefined) {
    warnings.push({
      code: 'CONTAINS_CONTACT_DATA',
      found: contact.slice(0, ISSUE_TEXT_MAX_LENGTH),
    })
  }
  // A plural form the language never selects is dead text (i18next never asks for it): a warning, not a refusal, since
  // the catalogues carry some (the `_many` of English).
  if (pluralKey !== null && !pluralCategoriesOf(locale).includes(pluralKey.form)) {
    warnings.push({ code: 'UNUSED_PLURAL_FORM', found: pluralKey.form })
  }
  const [valueLead, valueTrail] = outerWhitespace(value)
  const [referenceLead, referenceTrail] = outerWhitespace(referenceValue)
  if (valueLead !== referenceLead || valueTrail !== referenceTrail) {
    warnings.push({ code: 'OUTER_WHITESPACE_CHANGED' })
  }

  return {
    errors: TRANSLATION_VALUE_ERROR_CODES.flatMap((code) => errors[code]),
    warnings,
  }
}
